/**
 * Refresh queries by key — what a mutation's `onSuccess` calls.
 *
 * `@birdwatch/client` refreshes nothing on its own: every caller states which
 * queries its write made stale, with keys from the generated `queryKeys`.
 *
 * Keys are de-duplicated first: callers combine the sets of several changes
 * (`invalidate(...setA, ...setB)`), and invalidating one query twice in a tick
 * cancels the first refetch — a caller awaiting it would resume before the data
 * landed.
 *
 * @example
 * ```tsx
 * const invalidate = useInvalidateQueries();
 * const update = useUpdateTasksByTaskId({
 *   onSuccess: (_task, { params }) =>
 *     invalidate(queryKeys.getTasksByTaskId(params), queryKeys.getTasksList()),
 * });
 * ```
 */

import { hashKey, type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

export function useInvalidateQueries(): (
  ...keys: readonly QueryKey[]
) => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    async (...keys: readonly QueryKey[]) => {
      const unique = new Map(keys.map((key) => [hashKey(key), key]));
      await Promise.all(
        [...unique.values()].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
    [queryClient],
  );
}
